import type { Extension } from '@medplum/fhirtypes';

import { ELUVE_FHIR_NAMESPACE } from './constants';

export const REASSESSMENT_MARKER_EXTENSION_URL = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/reassessment-marker`;

type ReassessmentMarkerBase = {
  bookedManually: boolean;
};

export type ReassessmentMarker = ReassessmentMarkerBase &
  (
    | { afterVisits: number; afterDays?: never }
    | { afterVisits?: never; afterDays: number }
  );

export function reassessmentMarkerExtension(
  marker: ReassessmentMarker,
): Extension {
  return {
    url: REASSESSMENT_MARKER_EXTENSION_URL,
    extension: [
      marker.afterVisits !== undefined
        ? { url: 'afterVisits', valueInteger: marker.afterVisits }
        : { url: 'afterDays', valueInteger: marker.afterDays },
      { url: 'bookedManually', valueBoolean: marker.bookedManually },
    ],
  };
}

export function reassessmentMarkerOf(resource: {
  extension?: Extension[];
}): ReassessmentMarker | undefined {
  const ext = resource.extension?.find(
    (e) => e.url === REASSESSMENT_MARKER_EXTENSION_URL,
  );
  if (!ext) return undefined;
  const sub = (url: string) => ext.extension?.find((e) => e.url === url);
  const afterVisits = sub('afterVisits')?.valueInteger;
  const afterDays = sub('afterDays')?.valueInteger;
  const bookedManually = sub('bookedManually')?.valueBoolean ?? false;
  if (afterVisits !== undefined) {
    return afterDays === undefined
      ? { afterVisits, bookedManually }
      : undefined;
  }
  return afterDays !== undefined ? { afterDays, bookedManually } : undefined;
}

export function withReassessmentMarker<T extends { extension?: Extension[] }>(
  resource: T,
  marker: ReassessmentMarker,
): T {
  const others = (resource.extension ?? []).filter(
    (e) => e.url !== REASSESSMENT_MARKER_EXTENSION_URL,
  );
  return {
    ...resource,
    extension: [...others, reassessmentMarkerExtension(marker)],
  };
}
